import { useState, FormEvent } from "react";
import { Loader2, Upload, Image as ImageIcon, Wallet, CheckCircle, ExternalLink } from "lucide-react";
import { Contract, parseEther } from "ethers";
import { useWallet } from "../contexts/WalletContext";
import { NFT_ADDRESS, MARKETPLACE_ADDRESS, PhygitalNFTAbi, NFTMarketplaceAbi, DEPLOYED_CHAIN_ID } from "../contracts";
import { uploadToPinata, PINATA_GATEWAY } from "../utils/ipfs";
import Toast from "../components/Toast";

interface ToastState { message: string; type: "success"|"error"|"info"|"pending"; txHash?: string; }
interface MintResult { tokenId: string; metaURI: string; txHash: string; listTxHash: string; price: string; }
interface Attribute  { trait_type: string; value: string; }

async function uploadMetadata(name: string, description: string, imageURI: string, attributes: Attribute[]): Promise<string> {
  const metadata = { name, description, image: imageURI, attributes };
  const blob = new Blob([JSON.stringify(metadata, null, 2)], { type: "application/json" });
  const file = new File([blob], `${name.replace(/\s+/g, "-").toLowerCase()}.json`, { type: "application/json" });
  const cid  = await uploadToPinata(file, `${name} metadata`);
  return `ipfs://${cid}`;
}

const POLYGONSCAN = "https://polygonscan.com";

export default function MintNFTPage() {
  const { isConnected, signer, chainId } = useWallet();
  const [form,       setForm]       = useState({ name: "", description: "", recipient: "", price: "" });
  const [attributes, setAttributes] = useState<Attribute[]>([{ trait_type: "", value: "" }]);
  const [file,       setFile]       = useState<File | null>(null);
  const [preview,    setPreview]    = useState("");
  const [loading,    setLoading]    = useState(false);
  const [toast,      setToast]      = useState<ToastState | null>(null);
  const [mintResult, setMintResult] = useState<MintResult | null>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const addAttr    = () => setAttributes(a => [...a, { trait_type: "", value: "" }]);
  const removeAttr = (i: number) => setAttributes(a => a.filter((_, idx) => idx !== i));
  const updateAttr = (i: number, field: keyof Attribute, val: string) =>
    setAttributes(a => a.map((attr, idx) => idx === i ? { ...attr, [field]: val } : attr));

  const handleMint = async (e: FormEvent) => {
    e.preventDefault();
    if (!signer || !file) return;
    if (!form.price || parseFloat(form.price) <= 0) {
      setToast({ message: "Enter a listing price in POL", type: "error" }); return;
    }
    setMintResult(null);
    try {
      setLoading(true);

      // Step 1 — upload image
      setToast({ message: "Step 1/4 — Uploading image to Pinata…", type: "pending" });
      const imageCID = await uploadToPinata(file, `${form.name.replace(/\s+/g, "-").toLowerCase()}-image`);
      const imageURI = `ipfs://${imageCID}`;

      // Step 2 — upload metadata with individual URI
      setToast({ message: "Step 2/4 — Uploading metadata to Pinata…", type: "pending" });
      const validAttrs = attributes.filter(a => a.trait_type.trim() && a.value.trim());
      const metaURI    = await uploadMetadata(form.name, form.description, imageURI, validAttrs);

      // Step 3 — mint on-chain (individual URI set per token)
      setToast({ message: "Step 3/4 — Minting NFT — confirm in MetaMask…", type: "pending" });
      const nftW      = new Contract(NFT_ADDRESS, PhygitalNFTAbi, signer);
      const recipient = form.recipient.trim() || await signer.getAddress();
      const mintTx    = await nftW.mint(recipient, metaURI);
      setToast({ message: "Mint submitted — waiting…", type: "pending", txHash: mintTx.hash });
      const receipt = await mintTx.wait();

      // Extract tokenId from Transfer event (from = 0x0)
      let tokenId = "?";
      for (const log of receipt.logs ?? []) {
        try {
          const parsed = nftW.interface.parseLog({ topics: log.topics as string[], data: log.data });
          if (parsed?.name === "Transfer" && parsed.args[0] === "0x0000000000000000000000000000000000000000") {
            tokenId = parsed.args[2].toString(); break;
          }
        } catch { /* skip */ }
      }

      // Step 4 — approve marketplace + list at chosen price
      setToast({ message: `Step 4/4 — Listing token #${tokenId} at ${form.price} POL…`, type: "pending" });
      const tokenIdBig = BigInt(tokenId);
      const approveTx = await nftW.approve(MARKETPLACE_ADDRESS, tokenIdBig);
      setToast({ message: "Approval submitted — waiting…", type: "pending", txHash: approveTx.hash });
      await approveTx.wait();

      const mktW  = new Contract(MARKETPLACE_ADDRESS, NFTMarketplaceAbi, signer);
      const price = parseEther(form.price);
      const listTx = await mktW.listNFT(NFT_ADDRESS, tokenIdBig, price);
      setToast({ message: "Listing submitted — waiting…", type: "pending", txHash: listTx.hash });
      await listTx.wait();

      setMintResult({ tokenId, metaURI, txHash: mintTx.hash, listTxHash: listTx.hash, price: form.price });
      setToast({ message: `Token #${tokenId} minted and listed at ${form.price} POL!`, type: "success", txHash: listTx.hash });

      // Reset
      setForm({ name: "", description: "", recipient: "", price: "" });
      setAttributes([{ trait_type: "", value: "" }]);
      setFile(null); setPreview("");
    } catch (err: any) {
      setToast({ message: err?.reason ?? err?.message ?? "Failed", type: "error" });
    } finally { setLoading(false); }
  };

  if (!isConnected) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        <Wallet className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-2xl font-semibold text-gray-700 mb-2">Connect Your Wallet</h2>
        <p className="text-gray-500">You must own the genesis NFT to mint</p>
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {toast && <Toast {...toast} chainId={chainId ?? DEPLOYED_CHAIN_ID} onClose={() => setToast(null)} />}
      <h1 className="text-3xl font-bold text-gray-800 mb-1">Mint NFT</h1>
      <p className="text-gray-500 mb-8">Genesis owner only — minted tokens are automatically listed on the marketplace</p>

      {mintResult && (
        <div className="mb-8 bg-green-50 border border-green-200 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
            <h3 className="font-semibold text-green-800 text-lg">Token #{mintResult.tokenId} minted & listed at {mintResult.price} POL</h3>
          </div>
          <p className="text-xs font-mono text-gray-600 break-all mb-3">{mintResult.metaURI}</p>
          <div className="flex gap-4 text-sm">
            <a href={`${POLYGONSCAN}/tx/${mintResult.txHash}`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-blue-600 hover:underline">
              Mint tx <ExternalLink className="w-3 h-3" />
            </a>
            <a href={`${POLYGONSCAN}/tx/${mintResult.listTxHash}`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-blue-600 hover:underline">
              List tx <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      )}

      <form onSubmit={handleMint} className="bg-white rounded-xl shadow-lg p-8 space-y-6">
        {/* Image */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Image</label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
            {preview ? (
              <div className="space-y-3">
                <img src={preview} alt="Preview" className="max-h-56 mx-auto rounded-lg object-contain" />
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 text-sm">
                  <Upload className="w-4 h-4" />Change
                  <input type="file" accept="image/*" onChange={handleFile} className="hidden" />
                </label>
              </div>
            ) : (
              <label className="cursor-pointer block">
                <ImageIcon className="w-14 h-14 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm mb-1">Click to upload</p>
                <p className="text-xs text-gray-400">PNG, JPG, GIF — up to 10 MB</p>
                <input type="file" accept="image/*" onChange={handleFile} className="hidden" required />
              </label>
            )}
          </div>
        </div>

        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <input type="text" required placeholder="NFT name"
            value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea required placeholder="Describe this NFT" rows={3}
            value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none" />
        </div>

        {/* Listing price */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Listing Price (POL)</label>
          <input type="number" required step="0.001" min="0.001" placeholder="e.g. 10"
            value={form.price} onChange={e => setForm({ ...form, price: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
          <p className="text-xs text-gray-400 mt-1">Token will be immediately listed on the marketplace at this price</p>
        </div>

        {/* Attributes */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700">Attributes <span className="text-gray-400 font-normal">(optional)</span></label>
            <button type="button" onClick={addAttr} className="text-sm text-blue-600 hover:text-blue-700 font-medium">+ Add trait</button>
          </div>
          <div className="space-y-2">
            {attributes.map((attr, i) => (
              <div key={i} className="flex gap-2 items-center">
                <input type="text" placeholder="Trait type" value={attr.trait_type}
                  onChange={e => updateAttr(i, "trait_type", e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                <input type="text" placeholder="Value" value={attr.value}
                  onChange={e => updateAttr(i, "value", e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                {attributes.length > 1 && (
                  <button type="button" onClick={() => removeAttr(i)} className="text-red-400 hover:text-red-600 text-xl px-1">×</button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Recipient */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Recipient <span className="text-gray-400 font-normal">(leave blank for your wallet)</span>
          </label>
          <input type="text" placeholder="0x…"
            value={form.recipient} onChange={e => setForm({ ...form, recipient: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
        </div>

        <div className="bg-blue-50 rounded-lg px-4 py-2.5 text-xs text-blue-600">
          Pinned to <span className="font-mono">{PINATA_GATEWAY}</span>
        </div>

        <button type="submit" disabled={loading || !file}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
          {loading
            ? <><Loader2 className="w-5 h-5 animate-spin" />Minting & Listing…</>
            : "Mint & List on Marketplace"}
        </button>
      </form>
    </div>
  );
}
